import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PrintIcon from "@mui/icons-material/Print";
import RefreshIcon from "@mui/icons-material/Refresh";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  FormControl,
  GlobalStyles,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import moment from "moment";
import { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import Link from "@mui/material/Link";
import { useRecoilValue } from "recoil";
import {
  useGetAccountStatementReportData,
  useGetAccountStatementReportDownload,
} from "src/api/reports";
import { useGetBranches } from "src/api/generic";
import { downloadAsXLS, getJournalEntryRoute } from "src/utils";
import DateRangeInput from "src/shared/components/date-range";
import {
  ReportControlBar,
  ReportDataTable,
  ReportPageHeading,
  ReportSectionCard,
  type ReportTableColumn,
} from "src/shared/components/report-layout";
import { authState } from "src/store/auth";
import RightArrow from "src/assets/svg-icons/aggregators/right_arrow.svg";
import { useGetAccountTree } from "src/api/tree/tree";
import AccountTreeDropdown, {
  type TreeNode,
} from "src/shared/components/account-tree-dropdown";

type DetailRow = {
  purchase_id: number;
  purchase_ref: string;
  branch_name: string;
  due_date: string;
  item_name: string;
  unit_name: string;
  quantity: number;
  net_total: number;
  fluctuation_percent: number;
};

type ItemDetailReportFilters = {
  startDate: string;
  endDate: string;
  branchId: string;
  itemId: string;
  itemType: string;
};

const AccountStatementReportPage = () => {
  const { t } = useTranslation("reports");
  const { t: generalT } = useTranslation("general");
  const { t: purchasingT } = useTranslation("purchasing");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const auth = useRecoilValue(authState);
  const companyName = auth.user?.company?.name;
  const [selectedTreeNode, setSelectedTreeNode] = useState<TreeNode>();

  const formValues = useMemo((): ItemDetailReportFilters => {
    return {
      startDate:
        searchParams.get("start_date") ||
        moment().startOf("month").format("yyyy-MM-DD"),
      endDate:
        searchParams.get("end_date") ||
        moment().endOf("month").format("yyyy-MM-DD"),
      branchId: searchParams.get("branch_id") || "",
      itemId: searchParams.get("item_id") || "",
      itemType: searchParams.get("item_type") || "account",
    };
  }, [searchParams]);

  const { control, setValue } = useForm<ItemDetailReportFilters>({
    values: formValues,
  });

  const {
    startDate,
    endDate,
    branchId: branchIdParam,
    itemId: itemIdParam,
    itemType: itemTypeParam,
  } = formValues;

  const { data: branchesData } = useGetBranches();
  const { data: accountTreeData, isLoading: isLoadingTree } =
    useGetAccountTree();

  const branchesHavePermission = branchesData?.data || [];
  const treeNodes: TreeNode[] = accountTreeData?.data ?? [];

  const itemIdValid = itemIdParam.length > 0;
  const isCategorySelected = itemTypeParam === "category";
  const branchIdNum = branchIdParam ? Number(branchIdParam) : NaN;
  const branchFilter = Number.isFinite(branchIdNum) ? [branchIdNum] : [];

  const {
    data: reportResponse,
    isFetching,
    refetch: refetchReport,
  } = useGetAccountStatementReportData({
    branches: branchFilter,
    start_date: startDate,
    end_date: endDate,
    chart_account_placement_id: isCategorySelected ? undefined : itemIdParam,
    chart_category_id: isCategorySelected ? itemIdParam : undefined,
    has_balance: true,
    enabled: false,
  });

  const {
    refetch: refetchDownloadReport,
    isFetching: isDownloadingReport,
  } = useGetAccountStatementReportDownload({
    branches: branchFilter,
    start_date: startDate,
    end_date: endDate,
    chart_account_placement_id: isCategorySelected ? undefined : itemIdParam,
    chart_category_id: isCategorySelected ? itemIdParam : undefined,
    enabled: false,
  });

  const allRows = useMemo(() => reportResponse?.data ?? [], [reportResponse]);

  // When has_balance=1 the backend prepends an opening-balance row (is_opening_balance: true).
  // Pull it out so it can be rendered as a distinct header row on top of the actual transactions.
  const openingBalanceRow = allRows[0]?.is_opening_balance ? allRows[0] : null;
  const rows = useMemo(
    () => (openingBalanceRow ? allRows.slice(1) : allRows),
    [allRows, openingBalanceRow],
  );

  const totalDebit = useMemo(
    () =>
      rows.reduce((sum: number, row: any) => {
        const val = parseFloat(row.debit) || 0;
        return sum + val;
      }, 0),
    [rows],
  );

  const totalCredit = useMemo(
    () =>
      rows.reduce((sum: number, row: any) => {
        const val = parseFloat(row.credit) || 0;
        return sum + val;
      }, 0),
    [rows],
  );

  // const filteredRows = useMemo(() => {
  //   const q = filterQuery;
  //   if (!q) return rows;
  //   return rows.filter(
  //     (r: any) =>
  //       r.purchase_ref.toLowerCase().includes(q) ||
  //       r.branch_name.toLowerCase().includes(q) ||
  //       r.item_name.toLowerCase().includes(q),
  //   );
  // }, [rows, filterQuery]);

  const syncDatesToUrl = (start: string, end: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("start_date", start);
    next.set("end_date", end);
    setSearchParams(next);
  };

  const syncItemToUrl = (id: string, type: string) => {
    const next = new URLSearchParams(searchParams);
    if (id) {
      next.set("item_id", id);
      next.set("item_type", type);
    } else {
      next.delete("item_id");
      next.delete("item_type");
    }
    setSearchParams(next);
  };

  const syncBranchToUrl = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set("branch_id", id);
    else next.delete("branch_id");
    setSearchParams(next);
  };

  const periodLabel = useMemo(() => {
    return t("reportDateRange", {
      start: moment(startDate).format("D MMM YYYY"),
      end: moment(endDate).format("D MMM YYYY"),
    });
  }, [t, startDate, endDate]);

  const handleDownload = () => {
    if (!itemIdValid) return;
    void refetchDownloadReport().then((res) => {
      if (res.data != null) {
        downloadAsXLS(`\uFEFF${res.data}`, "account-statement");
      }
    });
  };

  const handlePrint = () => {
    window.print();
  };

  const columns: ReportTableColumn<any>[] = useMemo(
    () => [
      {
        id: "date",
        header: generalT("date"),
        cell: (row) => (
          <Typography component="span" dir="auto">
            {row.date}
          </Typography>
        ),
      },
      {
        id: "journalId",
        header: purchasingT("journalId"),
        cell: (row) => {
          if (!row.reference) return null;
          return (
            <Link
              component={RouterLink}
              to={`/for-accountant/ledger?journal=${encodeURIComponent(row.reference)}`}
              underline="always"
              sx={{ fontSize: "inherit" }}
            >
              {row.reference}
            </Link>
          );
        },
      },
      {
        id: "source",
        header: purchasingT("source"),
        cell: (row) => (
          <Typography component="span" dir="auto">
            {row.source_text}
          </Typography>
        ),
      },
      {
        id: "reference",
        header: generalT("reference"),
        cell: (row) => {
          if (!row.related_reference) return null;
          const to = getJournalEntryRoute(row);
          return to ? (
            <Link
              component={RouterLink}
              to={to}
              underline="always"
              sx={{ fontSize: "inherit" }}
            >
              {row.related_reference}
            </Link>
          ) : (
            <Typography component="span" dir="auto">
              {row.related_reference}
            </Typography>
          );
        },
      },
      {
        id: "account",
        header: generalT("account"),
        cell: (row) => (
          <Typography component="span" dir="auto">
            {row.account}
          </Typography>
        ),
      },
      {
        id: "parentAccount",
        header: purchasingT("parentAccount"),
        align: "right",
        cell: (row) => (
          <Typography component="span" dir="auto">
            {row.parent_account}
          </Typography>
        ),
      },
      {
        id: "description",
        header: generalT("description"),
        align: "right",
        cell: (row) => (
          <Box component="span" fontWeight={600}>
            {row.description}
          </Box>
        ),
      },
      {
        id: "debit",
        header: generalT("debit"),
        align: "right",
        cell: (row) => (
          <Typography component="span" dir="auto">
            {row.debit}
          </Typography>
        ),
      },
      {
        id: "credit",
        header: generalT("credit"),
        cell: (row) => (
          <Typography component="span" dir="auto">
            {row.credit}
          </Typography>
        ),
      },
      {
        id: "balance",
        header: generalT("balance"),
        align: "right",
        cell: (row) => (
          <Typography component="span" dir="auto">
            {row.balance}
          </Typography>
        ),
      },
      {
        id: "branch",
        header: generalT("branch"),
        cell: (row) => (
          <Typography component="span" dir="auto">
            {row.branch}
          </Typography>
        ),
      },
      {
        id: "createdBy",
        header: generalT("createdBy"),
        cell: (row) => (
          <Typography component="span" dir="auto">
            {row.created_by?.name}
          </Typography>
        ),
      },
    ],
    [],
  );

  // Column layout for the opening-balance row: label spans up to the Balance column,
  // the value sits under Balance, and the remaining columns stay empty.
  const balanceColIndex = columns.findIndex((column) => column.id === "balance");
  const trailingColSpan = columns.length - balanceColIndex - 1;

  return (
    <Box p={{ xs: 2, sm: "12px 32px 32px" }} maxWidth="100%">
      <GlobalStyles
        styles={{
          "@media print": {
            "body *": { visibility: "hidden" },
            "#item-detail-print-root, #item-detail-print-root *": {
              visibility: "visible",
            },
            "#item-detail-print-root": {
              position: "absolute",
              left: 0,
              top: 0,
              width: "100%",
            },
          },
        }}
      />

      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        color="#101828"
        mb={1.5}
        sx={{ "@media print": { display: "none" } }}
      >
        <Typography
          fontSize="0.9rem"
          color="#475467"
          onClick={() => navigate("/reports")}
          sx={{ cursor: "pointer" }}
        >
          {t("thirdEyeReport")}
        </Typography>
        <img src={RightArrow} alt="" style={{ width: 12, height: 12 }} />
        <Box borderRadius="4px" bgcolor="#EAECF0" px={1} py={0.5}>
          <Typography color="#344054" fontWeight={600} fontSize="0.85rem">
            {t("accountStatement")}
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ "@media print": { display: "none" } }} mb={2}>
        <ReportControlBar
          left={
            <>
              <Controller
                name="branchId"
                control={control}
                render={({ field }) => (
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel id="item-detail-branch" shrink>
                      {generalT("branch")}
                    </InputLabel>
                    <Select
                      {...field}
                      labelId="item-detail-branch"
                      label={generalT("branch")}
                      onChange={(e) => {
                        const v = e.target.value as string;
                        field.onChange(v);
                        syncBranchToUrl(v);
                      }}
                      displayEmpty
                    >
                      <MenuItem value="">
                        <em>{t("allBranches")}</em>
                      </MenuItem>
                      {branchesHavePermission.map((branch) => (
                        <MenuItem key={branch.id} value={String(branch.id)}>
                          {branch.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />

              <AccountTreeDropdown
                variant="textfield"
                treeData={treeNodes}
                isLoading={isLoadingTree}
                allowParentSelect
                value={selectedTreeNode}
                label={generalT("account")}
                placeholder={t("selectAccountOrCategory")}
                size="small"
                sx={{ minWidth: 260 }}
                onSelect={(node) => {
                  setSelectedTreeNode(node);
                  const nodeType =
                    node.node_type === "category" ||
                    node.node_type === "subcategory"
                      ? "category"
                      : "account";
                  const value = String(
                    node.node_type === "account"
                      ? node.chart_account_placement_id
                      : node.id,
                  );
                  setValue("itemId", value);
                  syncItemToUrl(value, nodeType);
                }}
                onClear={() => {
                  setValue("itemId", "");
                  syncItemToUrl("", "account");
                }}
              />

              <Box sx={{ minWidth: 250 }}>
                <Controller
                  name="startDate"
                  control={control}
                  render={({ field }) => (
                    <DateRangeInput
                      startDate={field.value ? moment(field.value) : null}
                      startDateId="item-detail-start"
                      endDate={
                        formValues.endDate ? moment(formValues.endDate) : null
                      }
                      endDateId="item-detail-end"
                      onDatesChange={(arg: {
                        startDate: moment.Moment | null;
                        endDate: moment.Moment | null;
                      }) => {
                        const s = arg.startDate?.format("yyyy-MM-DD");
                        const e = arg.endDate?.format("yyyy-MM-DD");
                        if (s && e) {
                          field.onChange(s);
                          setValue("endDate", e);
                          syncDatesToUrl(s, e);
                        }
                      }}
                    />
                  )}
                />
              </Box>
            </>
          }
          center={
            <>
              <Button
                variant="contained"
                color="primary"
                startIcon={<RefreshIcon fontSize="small" />}
                onClick={() => void refetchReport()}
                disabled={!itemIdValid || isFetching}
                sx={{ height: 40, minWidth: 120 }}
              >
                {t("reportUpdate")}
              </Button>
            </>
          }
          right={
            <>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<PrintIcon fontSize="small" />}
                onClick={handlePrint}
                sx={{
                  height: 40,
                  minWidth: 100,
                  borderColor: "#D0D5DD",
                  color: "#344054",
                }}
              >
                {t("print")}
              </Button>
              <LoadingButton
                variant="outlined"
                startIcon={<FileDownloadIcon fontSize="small" />}
                onClick={handleDownload}
                loading={isDownloadingReport}
                disabled={!itemIdValid}
                sx={{
                  height: 40,
                  minWidth: 100,
                  borderColor: "#D0D5DD",
                  color: "#344054",
                }}
              >
                {t("export")}
              </LoadingButton>
            </>
          }
        />
      </Box>

      <Box id="item-detail-print-root">
        <ReportPageHeading
          title={t("accountStatement")}
          subtitle={companyName}
          periodLabel={periodLabel}
        />

        <ReportSectionCard>
          {!itemIdValid ? (
            <Box p={4} textAlign="center">
              <Typography color="text.secondary">
                {t("selectAccountToViewReport")}
              </Typography>
            </Box>
          ) : (
            <ReportDataTable<DetailRow>
              columns={columns}
              rows={rows}
              getRowId={(row, i) => `${row.purchase_ref}-${i}`}
              isLoading={isFetching}
              leadingRow={
                openingBalanceRow ? (
                  <TableRow
                    sx={{
                      bgcolor: "#F9FAFB",
                      "& td": {
                        fontWeight: 700,
                        fontSize: "0.875rem",
                        borderColor: "#F0ECE8",
                        py: 1.5,
                      },
                    }}
                  >
                    <TableCell colSpan={balanceColIndex} align="left">
                      {generalT("openingBalance")}
                    </TableCell>
                    <TableCell align="right" sx={{ pr: 2 }}>
                      <Typography component="span" fontWeight={700} dir="auto">
                        {openingBalanceRow.balance}
                      </Typography>
                    </TableCell>
                    {trailingColSpan > 0 && (
                      <TableCell colSpan={trailingColSpan} />
                    )}
                  </TableRow>
                ) : undefined
              }
              emptyMessage={
                <Typography color="text.secondary" align="center" py={2}>
                  {generalT("noData")}
                </Typography>
              }
              footer={
                rows.length > 0 ? (
                  <TableRow
                    sx={{
                      "& td": {
                        fontWeight: 700,
                        fontSize: "0.875rem",
                        borderTop: "2px solid #EAECF0",
                        bgcolor: "#F9FAFB",
                        py: 1.5,
                      },
                    }}
                  >
                    <TableCell colSpan={7}>
                      <Typography fontWeight={700}>
                        {generalT("total")}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight={700} dir="auto">
                        {totalDebit.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={700} dir="auto">
                        {totalCredit.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell colSpan={3} />
                  </TableRow>
                ) : undefined
              }
            />
          )}
        </ReportSectionCard>
      </Box>
    </Box>
  );
};

export default AccountStatementReportPage;
